import * as React from 'react'
import { PostCard } from 'components/molecules'
import styled from 'styled-components'
import { Spinner } from 'components/atoms'
import {
  COLORS,
  BREAKPOINT_TABLET_SMALL,
  BREAKPOINT_TABLET_LARGE,
  BREAKPOINT_TABLET_MOBILE,
} from 'static/constants'
import { IPager, IUser } from 'static/interfaces'
import { postService } from 'core/services'
import { IJsonResponse } from 'core/JsonApiSerializer'
import { checkLikeable } from 'utils'
import { getSearchFiltersCookie, getSearchFiltersTotalGuestCount } from 'utils/searchFiltersCookie'

interface IFilterParam {
  area_ids?: number[]
  category_ids?: number[]
  tag_ids?: number[]
  post_types?: string[]
}

interface IPageParam {
  per: number
  page: number
}

interface IProps {
  perPage?: number
  filter: {
    area_ids?: number[]
    category_ids?: number[]
    tag_ids?: number[]
    post_types?: string[]
    include_member?: boolean
  }
  user?: IUser
  initial_posts?: IJsonResponse
  initial_pagination?: IPager
}

const DEFAULT_PER_PAGE = 12
/**
 * @param filter  { area_ids: number[], category_ids: number[], tag_ids: number[], post_types: string[] }
 * @param perPage 一回のロード（初回表示含む）で取得する件数
 */
export const FetchablePostList: React.FC<IProps> = ({
  filter,
  perPage,
  user,
  initial_posts,
  initial_pagination,
}) => {
  const [fetchedPosts, setFetchedPosts] = React.useState(() =>
    initial_posts ? postService.serializer.parseResourceData(initial_posts, initial_posts.data) : []
  )
  const [fetching, setFetching] = React.useState(false)
  const [pagination, setPagination] = React.useState<IPager | null>(initial_pagination ?? null)
  const firstRender = React.useRef(true)

  const filterParam: IFilterParam = React.useMemo(() => {
    const searchFilters = getSearchFiltersCookie()
    const guestCount = searchFilters ? getSearchFiltersTotalGuestCount(searchFilters.guests) : null
    return {
      area_ids: filter?.area_ids,
      category_ids: filter?.category_ids,
      tag_ids: filter?.tag_ids,
      post_types: filter?.post_types,
      include_member: filter?.include_member,
      date: searchFilters?.date ?? null,
      guests: guestCount !== null && guestCount > 0 ? guestCount.toString() : null,
    }
  }, [filter])

  /**
   * ページネーションのパラメータを取得
   * @returns { per: number, page: number }
   */
  const pageParam: IPageParam = React.useMemo(() => {
    const page = pagination ? Number(pagination.next_page) : 1

    return {
      per: perPage ?? DEFAULT_PER_PAGE,
      page,
    }
  }, [pagination, pagination?.next_page, perPage])

  const fetchPosts = async (page = pageParam.page) => {
    const endPage = page !== 1 && pagination && pagination.current_page === pagination.total_pages
    if (fetching || endPage) {
      return
    }

    setFetching(true)

    const result = await postService.search({ ...pageParam, page, ...filterParam })
    setFetchedPosts(currentPosts => {
      if (page > 1) {
        return [...currentPosts, ...result.posts]
      }
      return result.posts
    })
    setPagination(result.pagination)
    setFetching(false)
  }

  React.useEffect(() => {
    if (firstRender.current && initial_posts !== undefined) {
      firstRender.current = false
      return
    }
    firstRender.current = false
    setFetchedPosts([])
    setPagination(null)
    fetchPosts(1)
  }, [filter, initial_posts])

  return (
    <ExpandablePostListWrapper>
      <div className="PostCardList">
        {fetchedPosts.map(post => (
          <PostCard
            key={post.id}
            post={post}
            noMargin={true}
            likeable={checkLikeable(post, user)}
          />
        ))}
      </div>
      {pagination?.next_page && (
        <>
          <div className="FindMore">
            {fetching ? (
              <Spinner />
            ) : (
              <button className="FindMoreButton" onClick={() => fetchPosts()}>
                Find More
              </button>
            )}
          </div>
          {!fetching && fetchedPosts.length === 0 && (
            <div className="InfoContainer">
              <div className="NoPost">Posts Not Found</div>
            </div>
          )}
        </>
      )}
    </ExpandablePostListWrapper>
  )
}

const ExpandablePostListWrapper = styled.div`
  width: 100%;
  .PostCardList {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    grid-gap: 1.6rem;
    justify-content: center;
    @media (max-width: ${BREAKPOINT_TABLET_LARGE}px) {
      grid-template-columns: repeat(3, minmax(0, 1fr));
    }
    @media (max-width: ${BREAKPOINT_TABLET_SMALL}px) {
      grid-template-columns: repeat(2, minmax(0, 1fr));
      grid-gap: 1.2rem 0.8rem;
    }
    @media (max-width: ${BREAKPOINT_TABLET_MOBILE}px) {
      // Card CSS Override --------------------------------------------------
      .Info {
        padding: 1.2rem;
        letter-spacing: 0;
        .Header {
          .Title {
            line-height: 2rem;
            font-size: 1.4rem;
          }
          .HeaderInner,
          .Duration {
            line-height: 2rem;
            font-size: 1.3rem;
          }
        }
        .Price {
          font-size: 1.2rem;
          .subLabel {
            margin-right: 0.2rem;
          }
          .value {
            font-size: 1.5rem;
          }
          .type {
            font-size: 1.1rem;
            margin-left: 0.2rem;
          }
        }
      }
    }
  }
  .InfoContainer {
    width: 100%;
    text-align: center;
    height: 56px;
  }
  .NoPost {
    font-size: 16px;
    font-weight: bold;
    color: ${COLORS.subText};
  }

  .FindMore {
    width: 100%;
    margin-top: 50px;
    text-align: center;
    height: 56px;
    @media (max-width: ${BREAKPOINT_TABLET_MOBILE}px) {
      margin-top: 2.4rem;
    }

    .FindMoreButton {
      cursor: pointer;
      display: flex;
      justify-content: center;
      align-items: center;
      width: 100%;
      height: 100%;
      max-width: 476px;
      margin: 0 auto;
      background: ${COLORS.white};
      color: ${COLORS.primary};
      font-size: 16px;
      font-weight: bold;
      border: 1px solid ${COLORS.primary};
      border-radius: 12px;
      outline: none;
      transition: opacity 0.2s;
      &:hover {
        opacity: 0.6;
      }
    }
  }
`

export default FetchablePostList
